import { BookingData } from "../../../types/bookings";

export const buildEditBookingFormData = (
  data: BookingData,
  bookingId: number,
): FormData => {
  const formData = new FormData();

  const append = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === "") return;
    if (typeof value === "boolean" || typeof value === "number") {
      formData.append(key, value.toString());
    } else if (typeof value === "string") {
      formData.append(key, value);
    } else if (value instanceof File) {
      formData.append(key, value);
    }
  };

  // ── Booking meta ────────────────────────────────────────────────────────────
  append("booking_id", bookingId);
  append("booking_type", data.booking_type?.toUpperCase());
  append("status", data.status || "pending");
  append("total", data.total?.toFixed(2));
  append("payment_method", data.payment_method || "pending");
  append("payment_status", data.payment_status || "pending");
  append("account_status", data.account_status || "active");
  append("conference_type", data.conference_type);

  // ── Contact ─────────────────────────────────────────────────────────────────
  append("contact_id", data.contact_id ?? data.company_id);
  
  if (data.contacts !== undefined) {
    formData.append(
      "contacts",
      JSON.stringify(
        data.contacts.map((c) => ({
          contact_id: c.contact_id,
          role: c.role,
        })),
      ),
    );
  }
  append("company_id", data.company_id);
  append("email", data.email);
  append("salutation", data.client_salutation);
  append("phone", data.phone_numbers || data.phone);
  append("source_of_booking", data.source || data.source_of_booking);
  append("referral_partner_id", data.referral_partner_id);
  append("company_name", data.company_name);
  append("branch_name", data.branch_name);
  append("branch_rep", data.branch_rep);

  // ── Deceased (flat fields) ──────────────────────────────────────────────────
  append("deceased_list[0][first_name]", data.deceased_name);
  append("deceased_list[0][surname]", data.deceased_surname);
  append("deceased_list[0][second_name]", data.second_name);
  append("deceased_list[0][date_of_birth]", data.deceased_dob);
  append("deceased_list[0][date_of_death]", data.deceased_dod);
  append("deceased_list[0][gender]", data.gender);
  append("deceased_list[0][salutation]", data.deceased_salutation);
  append("deceased_list[0][nickname]", data.deceased_nickname);
  append("deceased_list[0][relationship_to_deceased]", data.relationship_to_deceased);
  append("deceased_list[0][religion]", data.religion);
  append("deceased_list[0][denomination]", data.denomination);
  // Send existing record id so backend PATCHes rather than creates
  if (data.deceased_id) {
    append("deceased_list[0][id]", data.deceased_id);
  }
  // Image — new file takes priority, else preserve existing URL
  if (data.deceasedImageFile instanceof File) {
    formData.append(
      "deceased_list[0][person_image]",
      data.deceasedImageFile,
      data.deceasedImageFile.name,
    );
  } else if (data.photo_url) {
    append("deceased_list[0][person_image]", data.photo_url);
  }

  // ── Package ─────────────────────────────────────────────────────────────────
  if (data.package) {
    if (data.package_item?.id) {
      append("package_item[id]", data.package_item.id);
    }
    append("package_item[name]", data.package.name);
    append("package_item[description]", data.package.contents?.[0] || "Package");
    append("package_item[package]", data.package.id);
    append("package_item[price]", data.package.price?.toFixed(2));

    // Additional services
    const additionalServiceCategories = [
      "AV Equipment",
      "Audio Visual Equipment",
      "Design",
      "Other",
      "Screens",
      "Video & Audio",
      "Visual Presentation & Audio",
    ];
    const uniqueServiceIds = additionalServiceCategories
      .flatMap((cat) => (Array.isArray(data[cat]) ? (data[cat] as string[]) : []))
      .filter(Boolean)
      .filter((id, idx, arr) => arr.indexOf(id) === idx);

    uniqueServiceIds.forEach((id) => {
      formData.append("package_item[services][]", id);
    });
  }

  // ── Services (funeral / church / event / other) ─────────────────────────────
  const isConferenceOrOther =
    data.booking_type?.toUpperCase() === "CONFERENCE" ||
    data.booking_type?.toUpperCase() === "OTHER";

  if (!isConferenceOrOther) {
    const venueId = data.venue_ids?.[0];
    append("services_list[0][date_of_service]", data.date_of_service);
    append(
      "services_list[0][start_time]",
      data.start_time ? `${data.start_time}:00` : null,
    );
    append(
      "services_list[0][end_time]",
      data.end_time ? `${data.end_time}:00` : null,
    );
    if (venueId && parseInt(venueId) > 0) {
      append("services_list[0][venue_id]", parseInt(venueId));
    }
    append("services_list[0][mc_name]", data.mc_name);
    append("services_list[0][eulogist_name]", data.eulogist_name);
    append("services_list[0][church_name]", data.church_name);
    append("services_list[0][leader_preacher_name]", data.leader_preacher_name);
    append("services_list[0][leader_preacher_surname]", data.leader_preacher_surname);
    append("services_list[0][preacher_name]", data.preacher_name);
    append("services_list[0][preacher_surname]", data.preacher_surname);
    append("services_list[0][type_of_service]", data.type_of_service);
    append("services_list[0][service_notes]", data.service_notes);
    append("services_list[0][special_request]", data.special_request);
    append("services_list[0][service_address]", data.service_address);
    append("services_list[0][service_location]", data.service_location);
    append("services_list[0][service_city]", data.service_city || data.city);
    // Preserve existing service record id
    if (data.service_id) {
      append("services_list[0][id]", data.service_id);
    }
  }

  // ── Events (conference / other) ──────────────────────────────────────────────
  if (isConferenceOrOther) {
    const venueId = data.venue_ids?.[0];
    append("events_list[0][title]", data.title);
    append("events_list[0][hosts]", data.hosts);
    append("events_list[0][host_surname]", data.host_surname);
    append("events_list[0][guests]", data.guests);
    append("events_list[0][guest_surname]", data.guest_surname);
    if (venueId && parseInt(venueId) > 0) {
      append("events_list[0][venue_id]", parseInt(venueId));
    }
    append("events_list[0][start_date_of_event]", data.start_date_of_event);
    append("events_list[0][end_date_of_event]", data.end_date_of_event);
    append(
      "events_list[0][start_time]",
      data.start_time ? `${data.start_time}:00` : null,
    );
    append(
      "events_list[0][end_time]",
      data.end_time ? `${data.end_time}:00` : null,
    );
    append("events_list[0][expected_audience_size]", data.expected_audience_size);
    append("events_list[0][access_type]", data.access_type);
    append("events_list[0][enable_livestreaming]", data.enable_livestreaming);
    append("events_list[0][record_stream]", data.record_stream);
    append("events_list[0][streaming_platform]", data.streaming_platform);
    append("events_list[0][session_to_stream]", data.session_to_stream);
    append("events_list[0][specific_sessions]", data.specific_sessions);
    append("events_list[0][provide_on_demand_access]", data.provide_on_demand_access);
    append("events_list[0][retention_duration]", data.retention_duration);
    append("events_list[0][require_custom_branding]", data.require_custom_branding);
    append("events_list[0][branding_requirements]", data.branding_requirements);
    append("events_list[0][conference_type]", data.conference_type);
    append("events_list[0][service_notes]", data.service_notes);
    append("events_list[0][special_request]", data.special_request);
    // Preserve existing event record id
    if (data.event_id) {
      append("events_list[0][id]", data.event_id);
    }
  }

  return formData;
};